import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { shortenStringWithTooltip, formatDateOrDash, dateFormate } from '../../helper';
import _CustomTooltip from '../../Tooltip/CustomTooltip';
import { BombiixCard } from '../BombiixCard';
import { NewCheckbox } from '../../PIM/common/NewCheckbox';
import { EditIcon, DeleteIcon, ApproveIcon, RejectIcon } from '../../../bombiix-icon';
import { BombiixInfoPopover } from '../../BombiixInfoPopover';
import BombiixModal from '../../BombiixModal/BombiixModal';
import { updateFactoryActiveStateAction as _updateFactoryActiveStateAction } from '../../../Pages/Supplier/store/Supplier.action';
import { AppDispatch, useAppDispatch } from '../../../hooks/useAppDispatch';

const CustomTooltip = _CustomTooltip as unknown as React.FC<{ tooltipText: React.ReactNode; children?: React.ReactNode }>;
const updateFactoryActiveStateAction = _updateFactoryActiveStateAction as unknown as (
  _factoryId: string,
  _supplierId: string,
  _isActive: boolean,
  _transactionMsg: (_key: string) => string,
) => (_dispatch: AppDispatch) => Promise<boolean>;

/**
 * The factory / production-location fields this card reads.
 */
export interface SupplierFactoryCardElement {
  factoryId?: string;
  factoryCode?: string;
  factoryName?: string;
  productionLocationName?: string;
  portOfLanding?: string;
  buildingNo?: string;
  streetAddress?: string;
  townCity?: string;
  zipCode?: string;
  country?: string;
  minOrderQty?: number | string;
  minOrderQtyType?: string;
  updatedAt?: string;
  isActive?: boolean;
}

interface SuppliersComponentCardProps {
  element: SupplierFactoryCardElement;
  edit: (factoryId: string | undefined) => void;
  onDelete: (factoryId: string | undefined) => void;
  CostingAddEditAccess?: boolean;
  CostingDeleteAccess?: boolean;
  isSelected?: boolean;
  onSelect?: (factoryId: string | undefined) => void;
  supplierId?: string;
  onActiveStateChange?: (_factoryId: string | undefined, _isActive: boolean) => void;
  isActive?: boolean;
}

/**
 * One factory or production-location card on the Partner Management factories tab.
 * @param {object} props - Component props.
 * @returns {React.ReactElement} The card.
 */
function SuppliersComponentCard({
  element,
  edit,
  onDelete,
  CostingAddEditAccess,
  CostingDeleteAccess,
  isSelected = false,
  onSelect,
  supplierId,
  onActiveStateChange,
  isActive: isActiveProp,
}: SuppliersComponentCardProps) {
  if (element?.minOrderQtyType && !element?.minOrderQty) {
    element.minOrderQtyType = '';
  }
  const { t } = useTranslation('modules/partner-overview');
  const dispatch = useAppDispatch();
  const [selectedTier, setSelectedTier] = useState('Tier1');
  const [showActiveStateConfirm, setShowActiveStateConfirm] = useState(false);
  const [activeStateSaving, setActiveStateSaving] = useState(false);

  useEffect(() => {
    try {
      const storedTier = localStorage.getItem('SupplierSelectedTier');
      if (storedTier) {
        setSelectedTier(storedTier);
      }
    } catch (error) {
      setSelectedTier('Tier1');
    }
  }, []);

  const isProductionLocationTier = selectedTier !== 'Tier1';

  const displayName = isProductionLocationTier ? element?.productionLocationName || element?.factoryName || '' : element?.factoryName || '';

  const fullAddress = element?.buildingNo ? `${element.buildingNo}, ${element.streetAddress}, ${element.townCity}, ${element.zipCode}, ${element.country}` : '';

  const isActive = (isActiveProp ?? element?.isActive) !== false;

  /**
   * Stores the opposite active state and reports it to the parent on success.
   */
  const confirmActiveStateChange = async () => {
    if (!element?.factoryId || !supplierId) return;
    setActiveStateSaving(true);
    const updated = await dispatch(updateFactoryActiveStateAction(element.factoryId, supplierId, !isActive, t));
    setActiveStateSaving(false);
    setShowActiveStateConfirm(false);
    if (updated && onActiveStateChange) {
      onActiveStateChange(element?.factoryId, !isActive);
    }
  };

  const stateKeyPrefix = isProductionLocationTier ? 'LOCATION' : 'FACTORY';
  const stateAction = isActive ? 'DEACTIVATE' : 'ACTIVATE';

  return (
    <div className="partner-card-cell">
      <BombiixCard className="partner-card partner-card--no-thumb">
        <BombiixCard.Header>
          <BombiixCard.Actions>
            {onSelect ? (
              <li>
                <NewCheckbox
                  id={`factory-select-${element?.factoryId}`}
                  checked={isSelected}
                  aria-label={t('PRODUCT_LISTING_CARD.NAME')}
                  onChange={() => onSelect(element?.factoryId)}
                />
              </li>
            ) : null}
            {CostingAddEditAccess && (
              <li>
                <CustomTooltip tooltipText={t('PRODUCT_LISTING_CARD.EDIT')}>
                  <button
                    type="button"
                    className="bombiix-icon-container"
                    onClick={() => {
                      edit(element.factoryId);
                    }}
                    aria-label="Edit"
                  >
                    <EditIcon size={24} />
                  </button>
                </CustomTooltip>
              </li>
            )}
            {CostingDeleteAccess && (
              <li>
                <CustomTooltip tooltipText={t('PRODUCT_LISTING_CARD.DELETE')}>
                  <button
                    type="button"
                    className="bombiix-icon-container bombiix-icon-container--red"
                    onClick={() => {
                      onDelete(element.factoryId);
                    }}
                    aria-label="delete"
                  >
                    <DeleteIcon size={24} />
                  </button>
                </CustomTooltip>
              </li>
            )}
            <li className="partner-card-actions__right">
              <CustomTooltip tooltipText={isActive ? t('SUPLIER_COMPONENTS.ACTIVE') : t('SUPLIER_COMPONENTS.INACTIVE')}>
                <button
                  type="button"
                  className={`bombiix-icon-container partner-card-status${isActive ? ' is-active' : ''}`}
                  aria-label={isActive ? t('SUPLIER_COMPONENTS.ACTIVE') : t('SUPLIER_COMPONENTS.INACTIVE')}
                  aria-pressed={isActive}
                  disabled={!CostingAddEditAccess}
                  onClick={() => setShowActiveStateConfirm(true)}
                >
                  {isActive ? <ApproveIcon size={24} /> : <RejectIcon size={24} />}
                </button>
              </CustomTooltip>
            </li>
            <li className="partner-card-actions__right">
              <BombiixInfoPopover
                placement="bottom"
                iconSize={24}
                ariaLabel={t('PRODUCT_LISTING_CARD.FACTORY_INFO')}
                rows={[{ label: t('PRODUCT_LISTING_CARD.LAST_UPDATED'), value: formatDateOrDash(element?.updatedAt, dateFormate) }]}
              />
            </li>
          </BombiixCard.Actions>
        </BombiixCard.Header>
        <BombiixCard.Body>
          <BombiixCard.Title>{element?.factoryCode || ''}</BombiixCard.Title>
          <BombiixCard.Meta>
            <li>
              <strong>{isProductionLocationTier ? t('NEW_PARTNER_OVERVIEW.PRODUCTION_LOCATION') : t('PRODUCT_LISTING_CARD.NAME')}: </strong>
              <span>{displayName}</span>
            </li>
            {!isProductionLocationTier && (
              <li>
                <strong>{t('PRODUCT_LISTING_CARD.PORT_OF_LADING')}: </strong>
                <span>{element?.portOfLanding || ''}</span>
              </li>
            )}
            <li className="partner-card-meta--stacked">
              <strong>{t('PRODUCT_LISTING_CARD.ADDRESS')}: </strong>
              <span>{fullAddress ? shortenStringWithTooltip(fullAddress, 110) : ''}</span>
            </li>
          </BombiixCard.Meta>
        </BombiixCard.Body>
      </BombiixCard>
      {showActiveStateConfirm && (
        <BombiixModal
          show={showActiveStateConfirm}
          title={t(`SUPLIER_COMPONENTS.${stateAction}_${stateKeyPrefix}_TITLE`)}
          onClose={() => setShowActiveStateConfirm(false)}
          onSave={confirmActiveStateChange}
          saveLabel={t(`SUPLIER_COMPONENTS.${stateAction}`)}
          saveVariant={isActive ? 'red' : 'blue'}
          saveDisabled={activeStateSaving}
          size="sm"
          className="partner-state-modal"
        >
          <p className="partner-state-modal__message">{t(`SUPLIER_COMPONENTS.${stateAction}_${stateKeyPrefix}_MESSAGE`)}</p>
        </BombiixModal>
      )}
    </div>
  );
}

export default SuppliersComponentCard;
